/* eslint-disable no-void-undefined-signatures/no-void-undefined-signatures, no-void-effect/no-void-effect */
export const listingInferenceReviewPortalPhase = {
  featureUnavailable: "featureUnavailable",
  queueEmpty: "queueEmpty",
  noListingSelected: "noListingSelected",
  loading: "loading",
  loadedWorkspace: "loadedWorkspace",
  noSelectedClaim: "noSelectedClaim",
  readDenied: "readDenied",
  loadFailed: "loadFailed",
  writeDeniedReadOnly: "writeDeniedReadOnly",
  submittingDecision: "submittingDecision",
  submitFailed: "submitFailed",
  submittedRefreshing: "submittedRefreshing",
  summaryUpdated: "summaryUpdated",
  unsafeFactPromotionRefused: "unsafeFactPromotionRefused",
} as const;

export type ListingInferenceReviewPortalPhase =
  (typeof listingInferenceReviewPortalPhase)[keyof typeof listingInferenceReviewPortalPhase];

export type ListingInferenceReviewPortalSummary = {
  readonly proposed: number;
  readonly confirmed: number;
  readonly rejected: number;
  readonly conflicted: number;
};

export type ListingInferenceReviewPortalClaimRow = {
  readonly id: string;
  readonly kind: string;
  readonly value: string;
  readonly disposition: string;
  readonly sourceRef: string;
  readonly speaker: string;
  readonly observedAt: string;
  readonly nonFactStatus: string;
};

export type ListingInferenceReviewPortalGapRow = {
  readonly question: string;
  readonly sourceRef: string;
  readonly status: string;
};

export type ListingInferenceReviewPortalStatusMessage = {
  readonly id: string;
  readonly message: string;
};

export type ListingInferenceReviewPortalProvenanceRow = {
  readonly label: string;
  readonly value: string;
};

export type ListingInferenceReviewPortalDecisionDraft = {
  readonly claimId: string;
  readonly targetDisposition: string;
  readonly reviewer: string;
  readonly reason: string;
};

export type ListingInferenceReviewPortalQueueItem = {
  readonly listingId: string;
  readonly label: string;
  readonly nextReview: string;
};

export type ListingInferenceReviewPortalWorkflowProps = {
  readonly phase: ListingInferenceReviewPortalPhase;
  readonly listingId: string;
  readonly hasProvider: boolean;
  readonly hasProgramBoundary: boolean;
  readonly reviewQueue: readonly ListingInferenceReviewPortalQueueItem[];
  readonly directListingId: string;
  readonly claims: readonly ListingInferenceReviewPortalClaimRow[];
  readonly gaps: readonly ListingInferenceReviewPortalGapRow[];
  readonly summary: ListingInferenceReviewPortalSummary;
  readonly selectedClaim: import("@ps-bridge/interop").OptionTs<ListingInferenceReviewPortalClaimRow>;
  readonly selectedClaimLabel: string;
  readonly decisionTargetMessage: string;
  readonly selectedClaimContextMessage: string;
  readonly errorMessages: readonly ListingInferenceReviewPortalStatusMessage[];
  readonly acceptedDecisionMessages: readonly ListingInferenceReviewPortalStatusMessage[];
  readonly provenanceRows: readonly ListingInferenceReviewPortalProvenanceRow[];
  readonly provenanceEmptyMessage: string;
  readonly filterDisposition: string;
  readonly filterKind: string;
  readonly includeGaps: boolean;
  readonly readOnly: boolean;
  readonly errorMessage: string;
  readonly acceptedDecision: string;
  readonly draft: ListingInferenceReviewPortalDecisionDraft;
  readonly onDirectListingIdChange: (value: string) => void;
  readonly onLoadDirectListing: () => void;
  readonly onSelectListing: (listingId: string) => void;
  readonly onFilterDispositionChange: (value: string) => void;
  readonly onFilterKindChange: (value: string) => void;
  readonly onIncludeGapsChange: (value: boolean) => void;
  readonly onSelectClaim: (claimId: string) => void;
  readonly onNextReview: () => void;
  readonly onDraftChange: (draft: ListingInferenceReviewPortalDecisionDraft) => void;
  readonly onRetryLoad: () => void;
  readonly onSubmitDecision: () => void;
  readonly onRetrySubmit: () => void;
  readonly onRequestUnsafeFactPromotion: () => void;
};

export type ListingInferenceReviewPortalRouteShellInput = {
  readonly portalEnabled: boolean;
  readonly hasRouteListingId: boolean;
  readonly hasReviewQueueItems: boolean;
  readonly canReadWorkspace: boolean;
};

export const listingInferenceReviewPortalPhases = [
  listingInferenceReviewPortalPhase.featureUnavailable,
  listingInferenceReviewPortalPhase.queueEmpty,
  listingInferenceReviewPortalPhase.noListingSelected,
  listingInferenceReviewPortalPhase.loading,
  listingInferenceReviewPortalPhase.loadedWorkspace,
  listingInferenceReviewPortalPhase.noSelectedClaim,
  listingInferenceReviewPortalPhase.readDenied,
  listingInferenceReviewPortalPhase.loadFailed,
  listingInferenceReviewPortalPhase.writeDeniedReadOnly,
  listingInferenceReviewPortalPhase.submittingDecision,
  listingInferenceReviewPortalPhase.submitFailed,
  listingInferenceReviewPortalPhase.submittedRefreshing,
  listingInferenceReviewPortalPhase.summaryUpdated,
  listingInferenceReviewPortalPhase.unsafeFactPromotionRefused,
] as const satisfies ReadonlyArray<ListingInferenceReviewPortalPhase>;

export const selectListingInferenceReviewPortalRouteShellPhase = ({
  portalEnabled,
  hasRouteListingId,
  hasReviewQueueItems,
  canReadWorkspace,
}: ListingInferenceReviewPortalRouteShellInput): ListingInferenceReviewPortalPhase => {
  if (!portalEnabled) {
    return listingInferenceReviewPortalPhase.featureUnavailable;
  }

  if (!hasRouteListingId) {
    return hasReviewQueueItems
      ? listingInferenceReviewPortalPhase.noListingSelected
      : listingInferenceReviewPortalPhase.queueEmpty;
  }

  if (!canReadWorkspace) {
    return listingInferenceReviewPortalPhase.readDenied;
  }

  return listingInferenceReviewPortalPhase.loading;
};
